import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Eye, EyeOff, Upload, FileText } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { sendAcademicNotifications } from "@/lib/sendAcademicNotifications";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { ClassTargeting, ExpiryControls } from "./AcademicFormControls";

interface AnnouncementForm {
  title: string;
  content: string;
  category: string;
  priority: string;
  published: boolean;
  expires_at: Date | null;
  expiry_mode: string;
  target_classes: string[];
  file_url: string;
}

const emptyForm: AnnouncementForm = {
  title: "", content: "", category: "general", priority: "normal",
  published: true, expires_at: null, expiry_mode: "none", target_classes: [],
  file_url: "",
};

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.xls,.xlsx";

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function toSentenceCase(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function AnnouncementsManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<AnnouncementForm>(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ["academic-announcements"],
    queryFn: async () => {
      const { data, error } = await supabase.from("announcements").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: availableClasses = [] } = useQuery({
    queryKey: ["available-classes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("courses").select("class").not("class", "is", null);
      if (error) throw error;
      return [...new Set(data.map((c: any) => c.class as string))].sort();
    },
  });

  const uploadFile = async (file: File): Promise<string> => {
    const ext = file.name.split(".").pop();
    const path = `announcements/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("academic-files").upload(path, file);
    if (error) throw error;
    // Store the path, not a public URL - we'll generate signed URLs when viewing
    return path;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: AnnouncementForm & { id?: string; file?: File | null }) => {
      setUploading(true);
      const isNewPublish = data.published && (!data.id || !editing?.published);
      let fileUrl = data.file_url;
      if (data.file) fileUrl = await uploadFile(data.file);
      const payload: any = {
        title: toSentenceCase(data.title.trim()),
        content: data.content,
        category: data.category,
        priority: data.priority,
        published: data.published,
        author_id: user!.id,
        expires_at: data.expires_at ? data.expires_at.toISOString() : null,
        target_classes: data.target_classes,
        file_url: fileUrl || null,
      };
      if (data.id) {
        const { error } = await supabase.from("announcements").update(payload).eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("announcements").insert(payload);
        if (error) throw error;
      }
      if (isNewPublish) {
        await sendAcademicNotifications({
          title: `📢 ${payload.title}`,
          message: data.content.slice(0, 200),
          link: "/academic",
          targetClasses: data.target_classes,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academic-announcements"] });
      toast({ title: editing ? "Announcement updated" : "Announcement created" });
      setFormOpen(false); setEditing(null); setSelectedFile(null); setUploading(false);
    },
    onError: (e: any) => {
      setUploading(false);
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("announcements").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academic-announcements"] });
      toast({ title: "Announcement deleted" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      const { error } = await supabase.from("announcements").update({ published }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academic-announcements"] });
      toast({ title: "Visibility updated" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const filtered = announcements.filter(a =>
    `${a.title} ${a.content}`.toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => { setEditing(null); setForm(emptyForm); setSelectedFile(null); setFormOpen(true); };
  const openEdit = (a: any) => {
    setEditing(a);
    setForm({
      title: a.title, content: a.content, category: a.category,
      priority: a.priority, published: a.published,
      expires_at: a.expires_at ? new Date(a.expires_at) : null,
      expiry_mode: a.expires_at ? "date" : "none",
      target_classes: a.target_classes || [],
      file_url: a.file_url || "",
    });
    setSelectedFile(null);
    setFormOpen(true);
  };

  const wordCount = countWords(form.content);
  const wordLimitExceeded = wordCount > 50;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative w-full sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search announcements..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Button size="sm" onClick={openNew}><Plus className="mr-2 h-4 w-4" />New Announcement</Button>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Announcement" : "New Announcement"}</DialogTitle></DialogHeader>
          <form onSubmit={e => { e.preventDefault(); if (wordLimitExceeded) return; saveMutation.mutate({ ...form, id: editing?.id, file: selectedFile }); }} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Write title in sentence case" />
              <p className="text-xs text-muted-foreground mt-1">Use sentence case (e.g. "Exam schedule update")</p>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} required rows={3} placeholder="Brief description (max 50 words)" />
              <p className={cn("text-xs mt-1", wordLimitExceeded ? "text-destructive" : "text-muted-foreground")}>
                {wordCount}/50 words{wordLimitExceeded && " — Exceeded limit"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="academic">Academic</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importance</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent / Priority</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Attach File (optional)</Label>
              <label className="flex items-center gap-2 cursor-pointer border rounded-md px-3 py-2 text-sm hover:bg-muted/50 transition-colors">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">{selectedFile ? selectedFile.name : "Choose file"}</span>
                <input type="file" accept={ACCEPTED_FILE_TYPES} className="hidden" onChange={e => setSelectedFile(e.target.files?.[0] || null)} />
              </label>
              <p className="text-xs text-muted-foreground">PDF, Word, or Excel</p>
            </div>
            {(form.file_url && !selectedFile) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <button type="button" className="underline truncate max-w-[300px]" onClick={async () => {
                  const url = await getSignedFileUrl(form.file_url);
                  if (url) window.open(url, "_blank");
                }}>Current file</button>
              </div>
            )}

            <ClassTargeting
              availableClasses={availableClasses}
              selectedClasses={form.target_classes}
              onChange={classes => setForm(f => ({ ...f, target_classes: classes }))}
            />

            <ExpiryControls
              expiryMode={form.expiry_mode}
              expiresAt={form.expires_at}
              onModeChange={mode => setForm(f => ({ ...f, expiry_mode: mode }))}
              onDateChange={date => setForm(f => ({ ...f, expires_at: date }))}
            />

            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={v => setForm(f => ({ ...f, published: v }))} />
              <Label>{form.published ? "Visible to students" : "Hidden from students"}</Label>
            </div>
            <Button type="submit" disabled={saveMutation.isPending || uploading || wordLimitExceeded} className="w-full">
              {uploading ? "Uploading..." : saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Announcements & Notices</CardTitle>
          <CardDescription>{filtered.length} announcements</CardDescription>
        </CardHeader>
        <CardContent className="table-responsive">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Importance</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Classes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium max-w-[200px] truncate">{a.title}</TableCell>
                  <TableCell>
                    <Badge variant={a.priority === "urgent" ? "destructive" : "secondary"}>
                      {a.priority === "urgent" ? "Urgent" : "Normal"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(a as any).file_url ? (
                      <button onClick={async () => {
                        const url = await getSignedFileUrl((a as any).file_url);
                        if (url) window.open(url, "_blank");
                      }}>
                        <FileText className="h-4 w-4 text-primary" />
                      </button>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-sm max-w-[120px] truncate">
                    {(a as any).target_classes?.length > 0
                      ? (a as any).target_classes.length === availableClasses.length ? "All" : (a as any).target_classes.join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.published ? "default" : "secondary"}>
                      {a.published ? "Published" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {(a as any).expires_at ? format(new Date((a as any).expires_at), "PP") : "—"}
                  </TableCell>
                  <TableCell className="text-sm">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="flex gap-1">
                    <Button variant="ghost" size="sm" title={a.published ? "Hide" : "Show"} onClick={() => toggleVisibilityMutation.mutate({ id: a.id, published: !a.published })}>
                      {a.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)}><Pencil className="h-3 w-3" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="h-3 w-3 text-destructive" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete announcement?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteMutation.mutate(a.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{isLoading ? "Loading..." : "No announcements found"}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
